import React from 'react';

type MarkVariant = 'icon' | 'wordmark' | 'full';

interface MelioBrandMarkProps {
  variant?: MarkVariant;
  height?: number;
  className?: string;
  /** Bone-ground icon for dark backgrounds (e.g. member rail). */
  onDark?: boolean;
  ariaLabel?: string;
}

const ASSETS: Record<MarkVariant, string> = {
  icon: '/brand/melio-icon.svg',
  wordmark: '/brand/melio-wordmark-v3-ink.png',
  full: '/brand/melio-full-logo.svg',
};

/** Bone colourways, for oxblood and Night grounds. */
const ASSETS_ON_DARK: Partial<Record<MarkVariant, string>> = {
  icon: '/brand/melio-icon-oxblood.svg',
  wordmark: '/brand/melio-wordmark-v3-bone.png',
};

/**
 * Intrinsic aspect (width / height) per variant.
 *
 * The wordmark's entry was WRONG: it carried the full lockup's 1746.58/1080 =
 * 1.617 while melio-wordmark.svg was 1920/380.55 = 5.045. Since `maxWidth` is
 * derived from this, the wordmark was being clamped to about a third of its
 * intended width everywhere it appeared — 36px where it wanted 111px on the
 * login screen. The v3 wordmark is 1502/366.
 */
const VIEWBOX_ASPECT: Record<MarkVariant, number> = {
  icon: 530.66 / 1080,
  wordmark: 1502 / 366,
  full: 1746.58 / 1080,
};

/**
 * Official Melio brand mark from marketing/brand-kit/ — do not redraw.
 */
export default function MelioBrandMark({
  variant = 'icon',
  height = 32,
  className = '',
  onDark = false,
  ariaLabel = 'Melio',
}: MelioBrandMarkProps) {
  const src = (onDark && ASSETS_ON_DARK[variant]) || ASSETS[variant];
  const width = Math.round(height * VIEWBOX_ASPECT[variant]);

  return (
    <img
      src={src}
      alt={ariaLabel}
      width={width}
      height={height}
      className={className}
      style={{ display: 'block', height, width: 'auto', maxWidth: width }}
      draggable={false}
    />
  );
}

export function MelioBrandLockup({
  iconHeight = 28,
  className = '',
  onDark = false,
}: {
  iconHeight?: number;
  className?: string;
  onDark?: boolean;
}) {
  return (
    <span className={`inline-flex items-center gap-2.5 ${className}`.trim()}>
      <MelioBrandMark variant="icon" height={iconHeight} onDark={onDark} ariaLabel="" />
      <MelioBrandMark variant="wordmark" height={Math.round(iconHeight * 0.72)} ariaLabel="Melio" />
    </span>
  );
}
